import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  ChevronLeft,
  Check,
  Shield,
  Star,
  Bot,
  Loader,
  Zap,
  TrendingUp,
  Users,
  BarChart3,
  Mail,
  Sparkles,
} from "lucide-react";

const PaymentPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const functions = getFunctions();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const handleCheckout = async () => {
    if (!user || !user.email) {
      setError("Please make sure you're logged in and have an email address.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const createCheckoutSessionFn = httpsCallable(
        functions,
        "createCheckoutSession"
      );
      const result = await createCheckoutSessionFn({
        email: user.email,
      });

      const data = result.data;
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Failed to get checkout URL");
      }
    } catch (err) {
      const errorMessage = err?.message || "Unknown error occurred";
      setError(`Error starting checkout: ${errorMessage}`);
      console.error(err);
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const features = [
    {
      icon: <Bot className="h-5 w-5" />,
      title: "25 AI Requests/day",
      description: "Higher daily AI request limit",
      color: "indigo",
    },
    {
      icon: <Zap className="h-5 w-5" />,
      title: "12 Quiz Slots",
      description: "Create up to 12 quizzes",
      color: "green",
    },
    {
      icon: <Sparkles className="h-5 w-5" />,
      title: "Full Vault Access",
      description: "Add quizzes from the vault",
      color: "purple",
    },
    {
      icon: <BarChart3 className="h-5 w-5" />,
      title: "Advanced Analytics",
      description: "Advanced analytics options",
      color: "yellow",
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: "Email Prizes",
      description: "Email prize capability",
      color: "pink",
    },
    {
      icon: <Users className="h-5 w-5" />,
      title: "10 Sessions per Quiz",
      description: "Up to 10 sessions per quiz",
      color: "blue",
    },
    {
      icon: <TrendingUp className="h-5 w-5" />,
      title: "Priority Support",
      description: "Priority email support",
      color: "orange",
    },
  ];

  const colorClasses = {
    indigo: "bg-indigo-100 text-indigo-600",
    green: "bg-green-100 text-green-600",
    purple: "bg-purple-100 text-purple-600",
    yellow: "bg-yellow-100 text-yellow-600",
    pink: "bg-pink-100 text-pink-600",
    blue: "bg-blue-100 text-blue-600",
    orange: "bg-orange-100 text-orange-600",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="border-b border-gray-200/50 bg-white/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Branding */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 blur opacity-70 animate-pulse"></div>
                <img
                  src="/QemplateFull.png"
                  alt="Qemplate Logo"
                  className="relative h-8 w-auto z-10"
                />
              </div>
              <div className="hidden sm:block h-6 w-px bg-gray-300"></div>
              <span className="hidden sm:block text-sm font-medium text-gray-600">
                Upgrade
              </span>
            </div>
            <button
              onClick={handleBackClick}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group px-3 py-2 rounded-lg hover:bg-gray-100"
            >
              <ChevronLeft
                size={20}
                className="mr-1 group-hover:-translate-x-1 transition-transform"
              />
              <span className="font-medium">Back</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 mb-4 shadow-lg">
            <Star className="h-8 w-8 text-white" fill="currentColor" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Upgrade to{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Pro
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Access additional features and higher limits to support your work.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left Column - Pricing Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden sticky top-24">
              {/* Pricing Header */}
              <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-8 text-white relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}
                ></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-sm font-medium opacity-90 mb-1">
                        Pro Plan
                      </div>
                      <div className="text-4xl font-bold">£4.99</div>
                      <div className="text-sm opacity-80 mt-1">per month</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                      <Star className="h-8 w-8" fill="currentColor" />
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/20">
                    <Check className="h-4 w-4" />
                    <span>Cancel anytime</span>
                  </div>
                </div>
              </div>

              {/* Pricing Content */}
              <div className="p-6">
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-4">
                  {/* Price Summary */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-700 font-medium">
                        Monthly Subscription
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        £4.99
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>Billed monthly</span>
                      <span className="flex items-center gap-1">
                        <Check className="h-3 w-3 text-green-500" />
                        No setup fees
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={handleCheckout}
                    disabled={loading || !user}
                    className={`w-full px-6 py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-200 ${
                      loading || !user
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:shadow-2xl hover:shadow-purple-500/50 transform hover:-translate-y-0.5 active:translate-y-0"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin" />
                        <span>Redirecting...</span>
                      </>
                    ) : (
                      <>
                        <span>Proceed to checkout</span>
                        <ChevronLeft size={18} className="rotate-180" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center px-4 mt-3">
                    By subscribing, you agree to our{" "}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-gray-700"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-gray-700"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                  {/* Security Badge */}
                  <div className="flex items-center justify-center gap-2 pt-2 text-xs text-gray-500">
                    <Shield className="h-4 w-4" />
                    <span>Secure checkout by Stripe</span>
                  </div>

                  {/* Trust Indicators */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>Cancel anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Features */}
          <div className="lg:col-span-2">
            {/* Feature Grid */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 mb-6">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  What's included
                </h2>
                <p className="text-gray-600">
                  Pro plan features at a glance
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="group p-4 rounded-xl border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`${
                          colorClasses[feature.color]
                        } rounded-lg p-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}
                      >
                        {feature.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Comparison Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              {/* Starter Plan */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-gray-200 p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Starter
                  </h3>
                  <div className="text-4xl font-bold text-gray-900 mb-1">
                    Free
                  </div>
                  <p className="text-sm text-gray-600">Forever</p>
                </div>
                <div className="space-y-3">
                  {[
                    "2 Quiz Slots",
                    "View-only Vault",
                    "3 AI Requests/day",
                    "1 Session per Quiz",
                    "Basic Analytics",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <Check className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pro Plan */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-300 p-8 relative shadow-xl hover:shadow-2xl transition-shadow">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg">
                    Pro plan
                  </span>
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
                  <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    £4.99
                  </div>
                  <p className="text-sm text-gray-600">per month</p>
                </div>
                <div className="space-y-3">
                  {[
                    "12 Quiz Slots",
                    "Full Vault Access",
                    "25 AI Requests/day",
                    "10 Sessions per Quiz",
                    "Advanced Analytics",
                    "Email Prizes",
                    "Priority Support",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-sm text-gray-900 font-medium"
                    >
                      <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Can I cancel anytime?
                  </h3>
                  <p className="text-sm text-gray-600">
                    Yes, you can cancel your subscription at any time. Your
                    account will remain active until the end of your billing
                    period.
                  </p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    What payment methods do you accept?
                  </h3>
                  <p className="text-sm text-gray-600">
                    We accept all major credit cards and debit cards through
                    Stripe's secure payment processing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
